import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { RecipeLite } from '../../../../shared/models/recipe/recipe-lite.model';
import { ConfigService } from '../../../../services/config.service';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { BehaviorSubject, merge, Observable, Subject } from 'rxjs';
import { RecipeService } from '../../../../services/recipe/recipe.service';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { VariantFormValue } from '../../../../shared/models/recipe/variant-form-value.model';
import { ApplianceGroup, DataRefsByKey, MarketingFood, Status, Unit, Utensil } from '../../../../shared/models/dataref.model';
import { TripletIdLite } from '../../../../shared/models/generic/id/triplet-id-lite.model';
import { NotificationService } from '../../../../services/notification.service';
import { RefDataService } from '../../../../services/ref-data.service';

@Component({
  selector: 'app-recipes-form-body',
  templateUrl: './recipes-form-body.component.html',
  styleUrls: ['./recipes-form-body.component.scss'],
  standalone: false,
})
export class RecipesFormBodyComponent implements OnChanges {
  @Input() recipeOriginal?: RecipeLite;
  @Input() variants?: RecipeLite[] | null;
  @Input() yieldUnits?: DataRefsByKey<Unit> | null;
  @Input() statusesRefData?: DataRefsByKey<Status> | null;
  @Input() applianceGroupsRefData?: DataRefsByKey<ApplianceGroup> | null;
  @Input() utensilsRefData?: DataRefsByKey<Utensil> | null;

  @Output() shouldReloadVariantsInfo = new EventEmitter<void>();
  @Output() variantDirty = new EventEmitter<boolean>();
  @Output() updateRecipe = new EventEmitter<Partial<RecipeLite>>();

  sortedVariants: RecipeLite[] = [];
  lang: string;
  selectedVariantId?: string;
  readonly variantDetails$: { [id: string]: Observable<RecipeLite> | Subject<RecipeLite> } = {};
  variantsIdAndLabels: { id: TripletIdLite; label: string }[] = [];
  marketingFoods$?: Observable<MarketingFood[]>;

  private readonly updateTriggersForVariants$: { [id: string]: Subject<Partial<RecipeLite>> } = {};

  constructor(
    private readonly configService: ConfigService,
    private readonly recipeService: RecipeService,
    private readonly notificationService: NotificationService,
    private readonly refDataService: RefDataService,
  ) {
    this.lang = configService.getCurrentLanguage();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.variants?.currentValue?.length && this.variants?.length) {
      this.sortedVariants = [...this.variants].sort((a: RecipeLite, b: RecipeLite) => a.yield.quantity - b.yield.quantity);

      // Initialize a map of observables that can be used to fetch each variant's details
      // As per Angular's Http client behavior, each http call will only be triggered once the corresponding observable is subscribed
      // This allows for a nice lazy-loading behavior where each variant details is loaded only once when the user first navigates in each tab
      this.variants.forEach((variant) => {
        if (!this.variantDetails$[variant.identifier.functionalId]) {
          const functionalId = variant.identifier.functionalId.startsWith('/')
            ? encodeURIComponent(variant.identifier.functionalId)
            : variant.identifier.functionalId; // fix recipe v1
          this.initializeObservablesForRecipe(
            variant,
            this.recipeService.getRecipe(`${variant.identifier.sourceSystem}/${functionalId}`).pipe(shareReplay(1)),
          );
        }
      });

      this.variantsIdAndLabels = this.getVariantIdAndLabelArray();
    }

    if (changes.recipeOriginal?.currentValue && this.recipeOriginal) {
      // recipeOriginal has its own separate treatment because we don't need to reload the details from backend
      // And we need to delegate the update to the parent component
      const originalRecipeDtails$ = this.variantDetails$[this.recipeOriginal.identifier.functionalId] as Subject<RecipeLite>;
      if (!originalRecipeDtails$) {
        this.variantDetails$[this.recipeOriginal.identifier.functionalId] = new BehaviorSubject(this.recipeOriginal);
      } else {
        originalRecipeDtails$.next(this.recipeOriginal);
      }

      const domain = this.recipeOriginal.domain;
      const market = this.recipeOriginal.market;
      const recipeLang = this.recipeOriginal.lang;

      this.marketingFoods$ = this.refDataService.getByKey('marketingFoods', true, domain, market, recipeLang).pipe(
        map((mFood) =>
          this.refDataService
            .translateReference(this.refDataService.toArray<MarketingFood>(mFood), market, recipeLang)
            .sort((mFood1: MarketingFood, mFood2: MarketingFood) => mFood1.translatedName?.localeCompare(mFood2.translatedName ?? '')),
        ),
        shareReplay(1),
      );
    }
  }

  getTabLabel(variant: RecipeLite): string {
    const yieldUnit = this.yieldUnits?.[variant.yield.unit];

    let unitName: string | undefined;

    if (variant.yield.quantity > 1) {
      unitName = yieldUnit?.pluralName?.[variant.market]?.[variant.lang];
    } else {
      unitName = yieldUnit?.name?.[variant.market]?.[variant.lang];
    }

    if (!unitName || unitName === '') {
      unitName = variant.yield.name;
    }

    return `${variant.yield.quantityDisplay} ${unitName}`;
  }

  getSelectedIndex(): number {
    const recipeId = this.selectedVariantId ?? this.recipeOriginal?.identifier.functionalId;

    return this.sortedVariants.findIndex((variant) => variant.identifier.functionalId === recipeId) ?? 0;
  }

  tabChange(event: MatTabChangeEvent): void {
    const variant = this.sortedVariants[event.index];

    if (!variant?.identifier) {
      return;
    }
     
    const functionalId = variant.identifier.functionalId.startsWith('/')
      ? variant.identifier.functionalId.replace('/', '')
      : variant.identifier.functionalId;

    window.history.replaceState({}, '', `recipes/edit/${variant.identifier.sourceSystem}/${functionalId}`);
  }

  variantDeleted(): void {
    // Force reloading of all variants after deletion
    this.variants?.forEach((variant) => {
      this.initializeObservablesForRecipe(
        variant,
        this.recipeService
          .getRecipe(`${variant.identifier.sourceSystem}/${encodeURIComponent(variant.identifier.functionalId)}`)
          .pipe(shareReplay(1)),
      );
    });

    // Forces Angular to instanciate a whole new mat-tab-group component to avoid active tab bugs after the variants are reloaded
    this.variants = null;
    this.shouldReloadVariantsInfo.emit();
  }
  // newValue represents the raw value from the form group.
  // The recipeService has to merge this raw value with the initial recipe before saving.
  updateVariant(event: { variant: RecipeLite; newValue: VariantFormValue }): void {
    if (event.variant.identifier.functionalId === this.recipeOriginal?.identifier.functionalId) {
      // Delegate updating the original recipe to the parent component
      this.updateRecipe.emit(this.recipeService.convertFormVariantToRecipeLite(event.variant, event.newValue));
    } else {
      this.updateTriggersForVariants$[event.variant.identifier.functionalId].next(
        this.recipeService.convertFormVariantToRecipeLite(event.variant, event.newValue),
      );
    }

    // Force reloading of other variants, as modifying one variant may affect the other on backend side
    this.variants?.forEach((variant) => {
      if (variant.identifier.functionalId !== event.variant.identifier.functionalId) {
        this.initializeObservablesForRecipe(
          variant,
          this.recipeService.getRecipe(`${variant.identifier.sourceSystem}/${variant.identifier.functionalId}`).pipe(shareReplay(1)),
        );
      }
    });
  }

  getVariantIdAndLabelArray(): { label: string; id: TripletIdLite }[] {
    return this.sortedVariants.map((variant: RecipeLite) => ({
      label: this.getTabLabel(variant),
      id: variant.identifier,
    }));
  }

  newVariant(newVariantId: string): void {
    this.selectedVariantId = newVariantId;
    this.shouldReloadVariantsInfo.emit();
  }

  private initializeObservablesForRecipe(recipe: RecipeLite, initialLoader: Observable<RecipeLite>): void {
    const updateTrigger$ = new Subject<Partial<RecipeLite>>();

    // In this step, we define our pipes in a way that any value that we will pass to updateTrigger$ will be sent to the update backend service.
    // Then, the return value from the backend will be emitted into the update$ observable.
    const update$ = updateTrigger$.pipe(
      switchMap((newValue: Partial<RecipeLite>) =>
        this.recipeService.updateRecipe(newValue).pipe(
          catchError((error) => {
            this.notificationService.sendAPIError(error);
            return initialLoader;
          }),
        ),
      ),
      tap((updatedRecipe) => {
        if (updatedRecipe?.status === 'DELETED') {
          // Forces Angular to instanciate a whole new mat-tab-group component to avoid active tab bugs after the variants are reloaded
          this.variants = null;
          this.shouldReloadVariantsInfo.emit();
        }
      }),
    );

    // Store the update trigger
    this.updateTriggersForVariants$[recipe.identifier.functionalId] = updateTrigger$;

    // Creates an observable that will at first take its inital value from initialLoader
    // and then replace this initial value by any new value emitted by update$.
    this.variantDetails$[recipe.identifier.functionalId] = merge(initialLoader, update$).pipe(shareReplay(1));
  }
}
